/**
 * Toast store — imperative API for the app-wide non-blocking notifications.
 *
 * Replaces `window.alert()` everywhere it was used for save-success / save-
 * error feedback. A single `<ToastHost />` rendered in `AppLayout` consumes
 * this store.
 *
 *   toast.show({ message: 'Pricing settings saved.', kind: 'success' });
 *   toast.show({ message: 'Failed to upload receipt.', kind: 'error' });
 *
 * Auto-dismiss timings (per kind):
 *   - info / success → 4 seconds
 *   - error          → 8 seconds (errors are actionable; give the user time)
 *
 * Up to MAX_VISIBLE toasts render at once. Excess are queued and surfaced
 * as older ones dismiss.
 */

import { create } from 'zustand';

export type ToastKind = 'info' | 'success' | 'error';

const DURATIONS_MS: Record<ToastKind, number> = {
  info: 4_000,
  success: 4_000,
  error: 8_000,
};

const MAX_VISIBLE = 3;

export interface ToastInput {
  message: string;
  kind?: ToastKind;
  /** Optional action — surfaces a button on the toast (e.g. "Undo" / "Retry"). */
  action?: { label: string; onClick: () => void };
}

export interface Toast extends ToastInput {
  id: string;
  kind: ToastKind;
}

interface ToastState {
  toasts: Toast[];
  show: (input: ToastInput) => string;
  dismiss: (id: string) => void;
}

let counter = 0;
const newId = (): string => {
  counter += 1;
  return `t${Date.now().toString(36)}-${counter}`;
};

export const useToastStore = create<ToastState>((set, get) => ({
  toasts: [],
  show: (input) => {
    const kind = input.kind ?? 'info';
    const id = newId();
    const toast: Toast = { ...input, kind, id };
    set((s) => ({ toasts: [...s.toasts, toast].slice(-MAX_VISIBLE * 2) }));
    // Auto-dismiss. Skipped when the host calls dismiss manually; the
    // timer becomes a no-op because the toast is already gone. Plain
    // setTimeout works in both jsdom + Node, which keeps the unit tests
    // straightforward — vi.useFakeTimers() can advance it.
    setTimeout(() => {
      get().dismiss(id);
    }, DURATIONS_MS[kind]);
    return id;
  },
  dismiss: (id) => set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })),
}));

/** Imperative toast API. Use anywhere — React component, hook, or service. */
export const toast = {
  show(input: ToastInput): string {
    return useToastStore.getState().show(input);
  },
  /** Convenience for the common case. */
  success(message: string): string {
    return useToastStore.getState().show({ message, kind: 'success' });
  },
  error(message: string): string {
    return useToastStore.getState().show({ message, kind: 'error' });
  },
  info(message: string): string {
    return useToastStore.getState().show({ message, kind: 'info' });
  },
  dismiss(id: string): void {
    useToastStore.getState().dismiss(id);
  },
};

export { MAX_VISIBLE };
